import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { todayKey } from "../lib/streak";
import { describeChanges } from "../lib/pendingDiff";
import { QUEUE_EVENT, SENT_EVENT, FLUSH_EVENT, flushCheerQueue, readCheerQueue, oldestQueuedAt } from "../lib/cheerQueue";
import { playChime, playPop, isChimeMuted, setChimeMuted } from "../lib/chime";
import { pushHistory } from "../lib/syncHistory";
import { toast } from "sonner";

// One family = many children. Each child: { id, panda, bamboo, completed, worksheets }.
// Guests keep children in localStorage; signed-in users sync each child to /api/children.
const KEY = "tycc_family_v2";
const LEGACY_KEY = "tycc_progress_v1";
const DIRTY_KEY = "tycc_dirty_children"; // child ids with local changes not yet saved to the server (offline queue)
const SINCE_KEY = "tycc_dirty_since"; // child id -> ISO of when it first became dirty
const readDirty = () => { try { return new Set(JSON.parse(localStorage.getItem(DIRTY_KEY) || "[]")); } catch { return new Set(); } };
const readSince = () => { try { return JSON.parse(localStorage.getItem(SINCE_KEY) || "{}"); } catch { return {}; } };
const markDirty = (id, on) => {
  const d = readDirty(); on ? d.add(id) : d.delete(id); localStorage.setItem(DIRTY_KEY, JSON.stringify([...d]));
  const s = readSince(); if (on) { if (!s[id]) s[id] = new Date().toISOString(); } else delete s[id]; localStorage.setItem(SINCE_KEY, JSON.stringify(s));
};
const oldestDirty = () => { const v = Object.values(readSince()); return v.length ? Math.min(...v.map((x) => new Date(x).getTime())) : null; };
export const STEPS = ["learn", "talk", "practice", "story", "dodont", "quiz"];

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const emptyChild = (panda, lessonDay = null) => ({ id: uid(), panda, bamboo: 0, completed: {}, worksheets: {}, activity: [], bambooLog: {}, stepLog: {}, lessonDay, weeklyGoal: null });
const defaultState = { children: [], activeChildId: null };

const ProgressContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...defaultState, ...JSON.parse(raw) };
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const p = JSON.parse(legacy);
      if (p?.panda?.name) {
        const child = { ...emptyChild(p.panda), bamboo: p.bamboo || 0, completed: p.completed || {}, worksheets: p.worksheets || {} };
        return { children: [child], activeChildId: child.id };
      }
    }
  } catch {
    /* ignore */
  }
  return defaultState;
};

export const progressPercent = (child, moduleId) => {
  const mod = child?.completed?.[moduleId] || {};
  const done = STEPS.filter((k) => mod[k] !== undefined && mod[k] !== false).length;
  return Math.round((done / STEPS.length) * 100);
};

const stripServerFields = (c) => ({ id: c.id, ownerId: c.owner_id || null, panda: c.panda, bamboo: c.bamboo || 0, completed: c.completed || {}, worksheets: c.worksheets || {}, activity: c.activity || [], bambooLog: c.bamboo_log || {}, stepLog: c.step_log || {}, lessonDay: c.lesson_day ?? null, weeklyGoal: c.weekly_goal ?? null, giftedStickers: c.gifted_stickers || [], giftsGiven: c.gifts_given || [] });
const payload = (c) => ({ panda: c.panda, bamboo: c.bamboo || 0, completed: c.completed || {}, worksheets: c.worksheets || {}, activity: c.activity || [], bamboo_log: c.bambooLog || {}, step_log: c.stepLog || {}, ...(c.lessonDay === null || c.lessonDay === undefined ? { clear_lesson_day: true } : { lesson_day: c.lessonDay }), ...(c.weeklyGoal === null || c.weeklyGoal === undefined ? { clear_weekly_goal: true } : { weekly_goal: c.weeklyGoal }) });
const withActivity = (c, reward = 0, steps = 0) => {
  const key = todayKey();
  const activity = c.activity || [];
  const log = c.bambooLog || {};
  const sl = c.stepLog || {};
  return { ...c, activity: activity.includes(key) ? activity : [...activity, key], bambooLog: reward ? { ...log, [key]: (log[key] || 0) + reward } : log, stepLog: steps ? { ...sl, [key]: (sl[key] || 0) + steps } : sl };
};

export const ProgressProvider = ({ children: reactChildren }) => {
  const { user } = useAuth();
  const [state, setState] = useState(load);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [following, setFollowing] = useState([]); // [{ follow_id, owner:{id,name}, children:[...] }]
  const [backOnline, setBackOnline] = useState(0);
  const [pending, setPending] = useState(() => readDirty().size); // children with changes not yet on the server
  const [queuedCheers, setQueuedCheers] = useState(() => readCheerQueue().length);
  const [sendingQueue, setSendingQueue] = useState(false); // cheer queue flush in progress
  const [saving, setSaving] = useState(0); // in-flight PUTs
  const [online, setOnline] = useState(typeof navigator === "undefined" || navigator.onLine);
  const [reconnectedAt, setReconnectedAt] = useState(0);
  const setDirty = (id, on) => { markDirty(id, on); setPending(readDirty().size); };
  const hadQueue = useRef(readDirty().size > 0); // true once a save failed / went offline -> toast when the queue drains
  const [retryAt, setRetryAt] = useState(null); // timestamp of the next automatic retry after an online save failure
  const [authExpired, setAuthExpired] = useState(false);
  const attempt = useRef(0);
  const retryTimer = useRef(null);
  const clearRetry = () => { clearTimeout(retryTimer.current); retryTimer.current = null; setRetryAt(null); };
  const scheduleRetry = () => {
    if (retryTimer.current) return;
    attempt.current += 1;
    const delay = Math.min(30000 * 2 ** (attempt.current - 1), 300000); // 30s -> 60s -> 2m -> 4m -> 5m
    setRetryAt(Date.now() + delay);
    retryTimer.current = setTimeout(() => { retryTimer.current = null; setRetryAt(null); setBackOnline((n) => n + 1); }, delay);
  };
  const lastUser = useRef(null);
  const lastSynced = useRef({}); // id -> JSON of last pushed payload
  const timer = useRef(null);

  const refreshFollowing = React.useCallback(async () => {
    if (!localStorage.getItem("tycc_token")) return;
    try {
      const r = await api.get("/following");
      setFollowing(r.data);
    } catch (e) {
      console.error("following load failed", e);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(state));
  }, [state]);

  const fetchChildren = React.useCallback(async () => {
    const r = await api.get("/children");
    const list = r.data.map(stripServerFields);
    list.forEach((c) => (lastSynced.current[c.id] = JSON.stringify(payload(c))));
    return list;
  }, []);

  // Re-pull the family from the server (e.g. right after accepting a co-parent invite)
  const refreshChildren = React.useCallback(async () => {
    if (!localStorage.getItem("tycc_token")) return;
    const list = await fetchChildren();
    setState((s) => ({ children: list, activeChildId: list.find((c) => c.id === s.activeChildId) ? s.activeChildId : list[0]?.id || null }));
  }, [fetchChildren]);

  // Login: push guest children to the account, then load the account's children.
  // Keyed on the user id (not the user object) so a refreshed /auth/me response never cancels an in-flight sync.
  const userId = user?.id || null;
  useEffect(() => {
    if (!userId) {
      if (lastUser.current) setState(defaultState); // logged out -> clean guest session
      lastUser.current = null;
      setSynced(false);
      setFollowing([]);
      lastSynced.current = {};
      return;
    }
    lastUser.current = userId;
    let cancelled = false;
    refreshFollowing();
    (async () => {
      try {
        setSyncing(true);
        const local = load();
        const created = {};
        // Only upload true guest children; anything with an ownerId already lives on the server
        for (const c of local.children.filter((c) => c?.panda?.name && !c.ownerId)) {
          const r = await api.post("/children", payload(c));
          created[c.id] = r.data.id;
        }
        const list = await fetchChildren();
        if (cancelled) return;
        const active = created[local.activeChildId] || (list.find((c) => c.id === local.activeChildId) ? local.activeChildId : list[0]?.id) || null;
        // Keep edits made while the fetch was in flight (e.g. lesson day changed right after load); the debounced push saves them.
        // Keep edits made while the fetch was in flight, and anything changed offline that never reached the server (DIRTY_KEY).
        const dirty = readDirty();
        setState((s) => ({
          children: list.map((srv) => {
            const cur = s.children.find((c) => c.id === srv.id);
            const snap = local.children.find((c) => c.id === srv.id);
            if (cur && snap && JSON.stringify(payload(cur)) !== JSON.stringify(payload(snap))) return { ...srv, ...cur };
            if (dirty.has(srv.id) && (cur || snap)) return { ...srv, ...(cur || snap) };
            return srv;
          }),
          activeChildId: active,
        }));
        setSynced(true);
      } catch (e) {
        console.error("family sync failed", e);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshFollowing, fetchChildren]);

  // Debounced push of changed children
  useEffect(() => {
    if (!user || !synced) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      state.children.forEach((c) => {
        const json = JSON.stringify(payload(c));
        if (lastSynced.current[c.id] === json) return;
        const prevJson = lastSynced.current[c.id];
        setDirty(c.id, true);
        setSaving((n) => n + 1);
        api
          .put(`/children/${c.id}`, payload(c))
          .then(() => {
            lastSynced.current[c.id] = json; setDirty(c.id, false); attempt.current = 0; clearRetry(); setAuthExpired(false);
            pushHistory(describeChanges(prevJson ? JSON.parse(prevJson) : null, c).map((x) => `${c.panda?.name || "Child"} · ${x}`));
          })
          .catch((e) => {
            hadQueue.current = true;
            console.error("child save failed", e);
            if (e?.response?.status === 401) setAuthExpired(true);
            else if (navigator.onLine) scheduleRetry();
          })
          .finally(() => setSaving((n) => n - 1));
      });
    }, 600);
    return () => clearTimeout(timer.current);
  }, [state, user, synced, backOnline]);

  // One toast (+ soft chime) when a queued (offline / failed) batch finally reaches the server
  useEffect(() => {
    if (pending === 0 && queuedCheers === 0 && saving === 0 && hadQueue.current && user) {
      hadQueue.current = false;
      playChime();
      toast.success("Progress saved to the cloud", {
        id: "cloud-saved",
        action: { label: isChimeMuted() ? "Unmute chime" : "Mute chime", onClick: () => { const on = !isChimeMuted(); setChimeMuted(on); toast(on ? "Chime muted on this device" : "Chime back on"); } },
      });
    }
  }, [pending, queuedCheers, saving, user]);

  // Offline cheer queue: track its size for the sync chip and flush it whenever we're online
  useEffect(() => {
    const onChange = () => { const n = readCheerQueue().length; if (n > 0) hadQueue.current = true; setQueuedCheers(n); };
    const onSent = (e) => {
      const { kind, childName, hasPhoto, hasVoice } = e.detail;
      playPop();
      const extra = hasPhoto && hasVoice ? " (photo + voice)" : hasPhoto ? " (photo)" : hasVoice ? " (voice)" : "";
      pushHistory(kind === "reply" ? `${childName || "Child"}'s thank-you${extra}` : `Cheer for ${childName || "child"}${extra}`);
      toast.success(kind === "reply" ? `${childName ? childName + "'s" : "The"} thank-you was sent` : `Your cheer for ${childName || "them"} was sent`, { id: `sent-${e.detail.localId}` });
    };
    const onFlush = (e) => setSendingQueue(!!e.detail.active);
    window.addEventListener(QUEUE_EVENT, onChange);
    window.addEventListener(SENT_EVENT, onSent);
    window.addEventListener(FLUSH_EVENT, onFlush);
    return () => { window.removeEventListener(QUEUE_EVENT, onChange); window.removeEventListener(SENT_EVENT, onSent); window.removeEventListener(FLUSH_EVENT, onFlush); };
  }, []);
  useEffect(() => {
    if (user && online && readCheerQueue().length > 0) flushCheerQueue();
  }, [user, online, backOnline]);

  // Re-push anything that failed while offline
  useEffect(() => {
    const on = () => { setOnline(true); setReconnectedAt(Date.now()); attempt.current = 0; clearRetry(); setBackOnline((n) => n + 1); };
    const off = () => { setOnline(false); if (readDirty().size > 0 || readCheerQueue().length > 0) hadQueue.current = true; };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const updateActive = (fn) =>
    setState((s) => ({ ...s, children: s.children.map((c) => (c.id === s.activeChildId ? fn(c) : c)) }));

  const value = useMemo(() => {
    const active = state.children.find((c) => c.id === state.activeChildId) || null;
    const followedChildren = following.flatMap((f) => f.children.map((c) => ({ ...stripServerFields(c), ownerName: f.owner.name, followId: f.follow_id, readOnly: true })));
    return {
      // family
      children: state.children,
      followedChildren,
      following,
      refreshFollowing,
      refreshChildren,
      findChild: (id) => state.children.find((c) => c.id === id) || followedChildren.find((c) => c.id === id) || null,
      activeChild: active,
      activeChildId: state.activeChildId,
      syncing,
      isCloud: !!user,
      syncStatus: { pending: pending + queuedCheers, saving: saving > 0 || sendingQueue, sendingQueue, online, reconnectedAt, synced, retryAt, authExpired, oldestDirtyAt: oldestDirty(), oldestPendingAt: (() => { const t = [oldestDirty(), oldestQueuedAt()].filter(Boolean); return t.length ? Math.min(...t) : null; })() },
      retrySync: () => { attempt.current = 0; clearRetry(); setBackOnline((n) => n + 1); if (readCheerQueue().length) flushCheerQueue(); },
      pendingChanges: () => {
        const dirty = readDirty();
        const since = readSince();
        const byId = Object.fromEntries(state.children.map((c) => [c.id, c]));
        return {
          children: state.children.filter((c) => dirty.has(c.id)).map((c) => {
            const prev = lastSynced.current[c.id];
            return { child: c, since: since[c.id] || null, changes: describeChanges(prev ? JSON.parse(prev) : null, c) };
          }),
          cheers: readCheerQueue().map((q) => ({ ...q, child: byId[q.childId] || followedChildren.find((c) => c.id === q.childId) || null })),
        };
      },
      addChild: async (panda, lessonDay = null) => {
        let child = emptyChild(panda, lessonDay);
        if (user && synced) {
          try {
            const r = await api.post("/children", payload(child));
            child = stripServerFields(r.data);
            lastSynced.current[child.id] = JSON.stringify(payload(child));
          } catch (e) {
            console.error("add child failed", e);
            throw e;
          }
        }
        setState((s) => ({ children: [...s.children, child], activeChildId: child.id }));
        return child;
      },
      switchChild: (id) => setState((s) => ({ ...s, activeChildId: id })),
      updateChildPanda: (id, panda) => setState((s) => ({ ...s, children: s.children.map((c) => (c.id === id ? { ...c, panda } : c)) })),
      updateChild: (id, patch) => setState((s) => ({ ...s, children: s.children.map((c) => (c.id === id ? { ...c, ...patch } : c)) })),
      removeChild: async (id) => {
        if (user && synced) {
          try {
            await api.delete(`/children/${id}`);
          } catch (e) {
            console.error("remove child failed", e);
          }
        }
        delete lastSynced.current[id];
        setState((s) => {
          const children = s.children.filter((c) => c.id !== id);
          return { children, activeChildId: s.activeChildId === id ? children[0]?.id || null : s.activeChildId };
        });
      },
      // active child shortcuts (same API the lesson pages already use)
      panda: active?.panda || null,
      bamboo: active?.bamboo || 0,
      completed: active?.completed || {},
      worksheets: active?.worksheets || {},
      activity: active?.activity || [],
      lessonDay: active?.lessonDay ?? null,
      weeklyGoal: active?.weeklyGoal ?? null,
      stepLog: active?.stepLog || {},
      savePanda: (panda) => {
        if (active) updateActive((c) => ({ ...c, panda }));
        else setState((s) => {
          const child = emptyChild(panda);
          return { children: [...s.children, child], activeChildId: child.id };
        });
      },
      completeStep: (moduleId, step, val = true) =>
        updateActive((c) => {
          const mod = c.completed[moduleId] || {};
          if (mod[step] && step !== "quiz") return c;
          const alreadyHadQuiz = step === "quiz" && mod.quiz !== undefined;
          const reward = alreadyHadQuiz ? 0 : step === "quiz" ? 10 : 5;
          const next = { ...mod, [step]: val };
          const allDone = STEPS.every((k) => next[k] !== undefined && next[k] !== false);
          if (allDone && !next.certifiedAt) next.certifiedAt = new Date().toISOString();
          return withActivity({ ...c, bamboo: c.bamboo + reward, completed: { ...c.completed, [moduleId]: next } }, reward, alreadyHadQuiz ? 0 : 1);
        }),
      setWorksheetCell: (moduleId, key, val) =>
        updateActive((c) => ({ ...c, worksheets: { ...c.worksheets, [moduleId]: { ...(c.worksheets[moduleId] || {}), [key]: val } } })),
      moduleProgress: (moduleId) => progressPercent(active, moduleId),
      reset: () => setState(defaultState),
    };
  }, [state, syncing, user, synced, following, refreshFollowing, refreshChildren, pending, queuedCheers, saving, sendingQueue, online, reconnectedAt, retryAt, authExpired]);

  return <ProgressContext.Provider value={value}>{reactChildren}</ProgressContext.Provider>;
};

export const useProgress = () => {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error("useProgress must be used inside ProgressProvider");
  return ctx;
};
